import { customColors, customColorsRaw } from '@apollo/orbit';
import { range } from 'lodash';
import React, { useEffect, useMemo } from 'react';

import {
  Axis,
  CHAR_HEIGHT,
  useFilteredTicks,
  useHiddenTickIndexes,
} from 'src/components/charts/helpers/Axis';
import {
  usePercentileTicks,
  useYAxisTicks,
} from 'src/components/charts/helpers/axisTicks';
import { useCursorPosition } from 'src/components/charts/helpers/Cursor';
import {
  HistogramBucket,
  getBucketAvg,
  getBucketRange,
  getPercentile,
} from 'src/components/charts/helpers/histogram';
import { useLinearScale } from 'src/components/charts/helpers/useScale';
import { decodeSparseBuckets } from 'src/lib/decodeSparseBuckets';
import { format } from 'src/lib/format';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { Bars } from '../helpers/Bars';
import { SvgWrapper, useSvgLayout } from '../helpers/svgLayout';
import { HistogramStackConfig } from '../helpers/useQueryHistogramStackInfo';
import { YAxisLabel } from '../helpers/YAxisLabel';

import { HistogramTooltip, HistogramTooltipProps } from './HistogramTooltip';

export type StackCount = { value: number; label: string };
export interface HistogramData extends HistogramBucket {
  y: number;
  x: number;
  stackCounts: StackCount[];
  hasTrace: boolean;
}
function getDefaultIndexValue(
  index: number,
  stacks: number,
  durationBuckets: number[] | undefined,
) {
  return {
    index,
    count: 0,
    y: 0,
    x: index,
    stackCounts: Array(stacks)
      .fill(0)
      .map(() => ({ value: 0, label: '0' })),
    hasTrace: !!durationBuckets?.includes(index),
  };
}

export function usePrepareHistogramData<Data>({
  stacks,
  activeSeries,
  durationBuckets,
}: {
  stacks: HistogramStackConfig<Data>[];
  activeSeries: boolean[];
  durationBuckets?: number[];
}) {
  return useMemo(() => {
    const values = {
      maxYValue: 0,
      maxXValue: 0,
      minXValue: Infinity,
    };
    const cache: { [key: number]: HistogramData } = {};
    stacks.forEach(({ getValue, data }, stackIndex) => {
      data.forEach((reportItem) => {
        if (!activeSeries[stackIndex]) return;
        decodeSparseBuckets(getValue(reportItem).sparseBuckets).forEach(
          ({ index, count }) => {
            const cached = getOrSetRecord(
              index,
              getDefaultIndexValue(index, stacks.length, durationBuckets),
              cache,
            );
            cached.count += count;
            cached.y = cached.count;
            const cachedCount = (cached.stackCounts[stackIndex] = cached
              .stackCounts[stackIndex] ?? { value: 0, label: '0' });
            cachedCount.value += count;
            cachedCount.label = format.metric(cachedCount.value, '0[.]0a');

            values.maxYValue = Math.max(values.maxYValue, cached.count);
            values.minXValue = Math.min(values.minXValue, index);
            values.maxXValue = Math.max(values.maxXValue, index);
          },
        );
      });
    });

    // If the max value is > the min value, we have data and the initial `Infinity` for min has been overwritten
    if (values.maxXValue > values.minXValue) {
      values.minXValue = Math.min(
        // include durationBuckets in min value incase there is a trace outside the data range
        ...(durationBuckets ?? []),
        // add an empty bucket to the beginning of the histogram
        Math.max(0, values.minXValue - 1),
      );
      values.maxXValue =
        // Include durationBuckets in max value incase there is a trace outside the data range.
        // And add an empty bucket to the end of the histogram
        Math.max(...(durationBuckets ?? []), values.maxXValue) + 1;

      // make sure ever index between the min and max values have a value
      range(values.minXValue, values.maxXValue + 1).forEach((index) => {
        cache[index] =
          cache[index] ??
          getDefaultIndexValue(index, stacks.length, durationBuckets);
      });
    } else {
      values.minXValue = 0;
    }
    return { data: Object.values(cache), ...values };
  }, [activeSeries, stacks, durationBuckets]);
}

export function Histogram<Key>({
  data: { data, maxXValue, maxYValue, minXValue },
  onClick,
  marker,
  className,
  stacks,
  onTraceSelect,
  activeSeries,
  yAxisLabel,
  'data-testid': dataTestId,
  tooltipLabelAs,
  hideCursorArrow = false,
}: {
  data: ReturnType<typeof usePrepareHistogramData>;
  onClick?: (index: number) => void;
  marker?: number;
  className?: string;
  stacks: HistogramStackConfig<Key>[];
  onTraceSelect?: (index: number) => void;
  activeSeries?: boolean[];
  yAxisLabel?: string;
  'data-testid'?: string;
  tooltipLabelAs?: React.ReactElement<HistogramTooltipProps>;
  hideCursorArrow?: boolean;
}) {
  const cursor = useCursorPosition();
  const bottomMargin = 2 * CHAR_HEIGHT + 6 + (onTraceSelect ? 27 : 0);
  const { setMaxYLabelLength, setFirstTick, ...svgLayout } = useSvgLayout({
    bottom: bottomMargin,
  });
  const { height, width } = svgLayout;

  const yAxisTicks = useYAxisTicks({
    height,
    maxValue: maxYValue,
    setMaxYLabelLength,
  });

  const xScale = useLinearScale({
    range: [0, width],
    domain: [minXValue, maxXValue],
  });

  const tickValues = usePercentileTicks(data);
  useEffect(() => {
    setFirstTick({
      labels: [`${format.ms(getBucketAvg(data[0]?.count ?? 0), 1, false)}`],
      offset: xScale(data[0]?.index ?? 0),
    });
  }, [tickValues, setFirstTick, data, xScale]);

  const cursorValueIndex = useMemo(() => {
    const foundIndex = data.findIndex(
      (bucket) => bucket.index === cursor?.latencyIndex,
    );
    return foundIndex === -1 ? null : foundIndex;
  }, [cursor?.latencyIndex, data]);

  const cursorTick = useMemo(
    () =>
      cursor?.latencyIndex
        ? {
            value: cursor.latencyIndex,
            labels: [format.ms(getBucketAvg(cursor.latencyIndex), 1, false)],
          }
        : undefined,
    [cursor?.latencyIndex],
  );
  const xAxisTicks = useFilteredTicks({
    tickValues,
    scale: xScale,
    defaultValue: 0,
  });
  const hiddenTickIndexs = useHiddenTickIndexes({
    ticks: xAxisTicks,
    scale: xScale,
    cursorTick,
    side: 'bottom',
  });

  const yScale = useLinearScale({ range: [height, 0], domain: [0, maxYValue] });
  const barsProps = useMemo(
    () => ({
      barColors: stacks.map(({ primaryColor, ...stack }) => ({
        fill: primaryColor,
        ...stack,
      })),
      onClick:
        onClick && (({ index }: (typeof data)[number]) => onClick(index)),
      getTooltip: ({ index, stackCounts }: (typeof data)[number]) => {
        const { rangeEndMs, rangeBeginMs } = getBucketRange(index);
        const percentile = getPercentile(rangeEndMs, data) * 100;

        // Somewhere (assuming in Chakra tooltip land) we must be incurring
        // a large performance penalty when copying props to the Tooltip.
        // For this reason, we should omit the data from the element that gets
        // created as the tooltip content otherwise it results in a painfully
        // laggy experience.
        // We could imagine a world where we just have a single
        // tooltip available for re-use and change the contents and where / when
        // it appears, but that's a big refactor.
        const stacksWithoutData = stacks.map((s) => {
          const { data: discardable, getValue, hasData, ...rest } = s;
          return rest;
        });

        return React.cloneElement<HistogramTooltipProps>(
          tooltipLabelAs ?? <HistogramTooltip />,
          {
            stackCounts,
            activeSeries,
            stacks: stacksWithoutData,
            rangeBeginMs,
            rangeEndMs,
            percentile,
          },
        );
      },
    }),
    [activeSeries, data, onClick, stacks, tooltipLabelAs],
  );

  if (!isMinLengthArray(1, data)) {
    return null;
  }

  return (
    <div className={className}>
      <div className="flex size-full">
        <div className="flex-grow">
          <SvgWrapper
            className="size-full"
            getCursor={(n) => ({
              latencyIndex: Math.round(xScale.invert(n[0])),
            })}
            data-testid={dataTestId}
            {...svgLayout}
          >
            <Axis
              tickValues={xAxisTicks}
              cursorTick={cursorTick}
              hiddenTicks={hiddenTickIndexs}
              scale={xScale}
              y={height}
              side="bottom"
              cursorContrastMode="high"
            />
            <Axis
              withLines={true}
              tickValues={yAxisTicks}
              scale={yScale}
              x={width}
              hideDomain
              side="right"
            />
            <Bars
              height={height}
              width={width}
              xScale={xScale}
              yScale={yScale}
              cursorValueIndex={cursorValueIndex}
              data={data}
              marker={marker}
              hideCursorArrow={hideCursorArrow}
              {...barsProps}
            />
            {onTraceSelect && (
              <line
                stroke={customColors.border.primary}
                x2={xScale(maxXValue)}
                transform={`translate(0, ${height + bottomMargin - 3})`}
              />
            )}
            {onTraceSelect &&
              data.map(({ index, hasTrace }) => {
                if (!hasTrace) return;
                const color =
                  cursor?.latencyIndex === index
                    ? marker === index
                      ? `rgb(${customColorsRaw.flowchart.c} / 0.6)`
                      : `rgb(${customColorsRaw.flowchart.d} / 0.6)`
                    : marker === index
                      ? `rgb(${customColorsRaw.flowchart.c} / 0.6)`
                      : `rgb(${customColorsRaw.flowchart.d} / 0.6)`;
                return (
                  <circle
                    key={String(index)}
                    transform={`translate(${xScale(index)}, ${
                      height + bottomMargin - 3
                    })`}
                    r={3}
                    strokeWidth={3}
                    fill={color}
                    stroke="transparent"
                    className="cursor-pointer"
                    onClick={() => onTraceSelect?.(index)}
                  />
                );
              })}
          </SvgWrapper>
        </div>
        {yAxisLabel ? (
          <YAxisLabel height={height - bottomMargin} value={yAxisLabel} />
        ) : null}
      </div>
    </div>
  );
}
